import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { getAllJobs } from '../data/jobs';

interface SearchResult {
  title: string;
  path: string;
  type: 'page' | 'blog' | 'job';
  description?: string;
}

export function Search() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);

  // Get job data
  const jobs = getAllJobs();

  // Search data
  const searchableContent: SearchResult[] = [
    { title: 'Home', path: '/', type: 'page', description: 'Welcome to Huxley Research' },
    { title: 'Research', path: '/research', type: 'page', description: 'Our latest research and breakthroughs' },
    { title: 'Models', path: '/models', type: 'page', description: 'Explore our AI models' },
    { title: 'FRACTAL 3B', path: '/models/fractal', type: 'page', description: 'Protein structure prediction with constraint-based folding' },
    { title: 'Projects', path: '/projects', type: 'page', description: 'Explore our active projects' },
    { title: 'Huxley', path: '/projects/huxley', type: 'page', description: 'Biological Computational Engine - Model-agnostic framework for biological AI' },
    { title: 'Company', path: '/company', type: 'page', description: 'About Huxley Research' },
    { title: 'About Us', path: '/about', type: 'page', description: 'Learn about our mission, values, and team' },
    { title: 'Careers', path: '/careers', type: 'page', description: 'Join our team' },
    { title: 'Safety & Ethics', path: '/safety', type: 'page', description: 'Our approach to AI safety' },
    { title: 'Privacy Policy', path: '/privacy', type: 'page', description: 'How we protect and handle your personal information' },
    { title: 'Terms of Service', path: '/terms', type: 'page', description: 'Terms and conditions for using our services' },
    { title: 'Documentation', path: '/documentation', type: 'page', description: 'API guides, tutorials, and technical resources' },
    { title: 'Blog', path: '/blog', type: 'page', description: 'Latest updates and insights' },
    { 
      title: 'Introducing Victor Academy: Your AI-Powered Study Companion',
      path: '/blog/victor-academy-launch',
      type: 'blog',
      description: 'Discover how Victor Academy is revolutionizing HSC preparation with AI-powered tools'
    },
    { 
      title: 'Launching Huxley Research: A new era of AI research',
      path: '/blog/odyssey-labs-launch',
      type: 'blog',
      description: 'Announcing our new research division'
    },
    // Add job positions dynamically
    ...jobs.map(job => ({
      title: `${job.title} - Remote`,
      path: `/careers/${job.id}`,
      type: 'job' as const,
      description: `Voluntary ${job.department} position with equity. ${job.description.substring(0, 80)}...`
    }))
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    if (query.length > 0) {
      const filtered = searchableContent.filter(item =>
        item.title.toLowerCase().includes(query.toLowerCase()) ||
        item.description?.toLowerCase().includes(query.toLowerCase())
      );
      setResults(filtered);
    } else {
      setResults([]);
    }
  }, [query]);

  const handleSelect = (result: SearchResult) => {
    navigate(result.path);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`hidden md:flex items-center px-4 py-2 text-sm rounded-full transition-colors duration-300 ${
          theme === 'dark'
            ? 'bg-dark-300 hover:bg-dark-200 text-gray-400'
            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
        }`}
      >
        <SearchIcon className="w-4 h-4 mr-2" />
        <span>Search</span>
        <span className="ml-2 px-1.5 py-0.5 text-xs rounded border border-gray-400 opacity-60">⌘K</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="min-h-screen px-4 text-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" />
            
            <div
              ref={searchRef}
              className={`relative inline-block w-full max-w-2xl text-left align-middle transition-all transform mt-20 ${
                theme === 'dark' ? 'bg-dark-200' : 'bg-white'
              } rounded-2xl shadow-2xl`}
            >
              <div className="flex items-center p-4 border-b border-gray-200 dark:border-dark-300">
                <SearchIcon className="w-5 h-5 mr-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className={`flex-1 bg-transparent border-none outline-none ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                  autoFocus
                />
                <button
                  onClick={() => setIsOpen(false)}
                  className={`p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-300`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="max-h-[60vh] overflow-y-auto">
                {results.length > 0 ? (
                  <div className="p-2">
                    {results.map((result, index) => (
                      <button
                        key={index}
                        onClick={() => handleSelect(result)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-colors duration-200 ${
                          theme === 'dark'
                            ? 'hover:bg-dark-300'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{result.title}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            result.type === 'job'
                              ? 'bg-green-100 text-green-700'
                              : result.type === 'blog'
                              ? 'bg-blue-100 text-blue-700'
                              : theme === 'dark'
                              ? 'bg-dark-300 text-gray-400'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {result.type === 'job' ? 'Career' : result.type}
                          </span>
                        </div>
                        {result.description && (
                          <p className={`text-sm mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {result.description}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                ) : query.length > 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No results found for "{query}"
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}